// Thin os-core wrapper around the @os/sign web pages, mirroring
// NotesWorkspacePage. Routed from App.tsx at /workspace/sign.
//
// The Sign module uses two top-level pages:
//   - SignDashboardPage: templates list + sent envelopes + inbox
//   - TemplateBuilderPage: PDF upload + field placement + send
//
// Navigation between them is driven by ?view=builder (builder) vs
// absent/other (dashboard). The SignSidebar sidebar receives the
// current DashTab and navigation callbacks from SignDashboardPage's
// own useSearchParams usage; the wrapper just mounts both pages and
// passes inter-page routing callbacks.
import { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import AppLayout from '../../components/layout/AppLayout'
import SignDashboardPage, { SignSidebar } from '@os/sign/web/pages/SignDashboardPage'
import TemplateBuilderPage from '@os/sign/web/pages/TemplateBuilderPage'

export default function SignWorkspacePage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const view = searchParams.get('view')
  const isBuilder = view === 'builder'

  const goToBuilder = useCallback((templateId?: string) => {
    setSearchParams((p) => {
      p.set('view', 'builder')
      if (templateId) p.set('template', templateId)
      else p.delete('template')
      p.delete('tab')
      p.delete('detail')
      return p
    })
  }, [setSearchParams])

  const goToDashboard = useCallback(() => {
    setSearchParams((p) => {
      p.delete('view')
      p.delete('template')
      return p
    })
  }, [setSearchParams])

  if (isBuilder) {
    const templateId = searchParams.get('template') ?? undefined
    return (
      <AppLayout>
        <TemplateBuilderPage templateId={templateId} onBack={goToDashboard} />
      </AppLayout>
    )
  }

  // Dashboard view — pass SignSidebar as Rail 2. The sidebar reads the
  // current ?tab from useSearchParams internally; we only provide the
  // inter-page navigation callback (onNew = go to builder with no templateId).
  const tab = (searchParams.get('tab') as 'templates' | 'sent' | 'tome' | null) ?? 'templates'
  const setTab = (t: 'templates' | 'sent' | 'tome') =>
    setSearchParams((p) => { p.set('tab', t); p.delete('detail'); return p })

  return (
    <AppLayout secondarySidebar={
      <SignSidebar tab={tab} onTab={setTab} onNew={() => goToBuilder()} />
    }>
      <SignDashboardPage
        onNewTemplate={() => goToBuilder()}
        onOpenBuilder={(templateId) => goToBuilder(templateId)}
      />
    </AppLayout>
  )
}
